import { useState, useCallback } from 'react';
import { exportService } from '../../../lib/supabase';
import { createLogger } from '../../../lib/logger';
import { EXPORTABLE_FIELDS } from '../types';

const log = createLogger('rsvp');

interface UseRSVPExportParams {
  inviteCode: string;
  userId: string | undefined;
  downloadCSV: (csvContent: string, filename: string) => void;
  setExportingData: (v: boolean) => void;
}

export function useRSVPExport({
  inviteCode,
  userId,
  downloadCSV,
  setExportingData,
}: UseRSVPExportParams) {
  const [showMainExportOptions, setShowMainExportOptions] = useState(false);
  const [selectedExportFields, setSelectedExportFields] = useState<Set<string>>(
    new Set(['guestName', 'attending'])
  );

  const setQuickExportPreset = useCallback((preset: 'basic' | 'full') => {
    if (preset === 'basic') {
      setSelectedExportFields(new Set(['guestName', 'guestSide', 'attending', 'guestCount']));
    } else if (preset === 'full') {
      setSelectedExportFields(new Set(EXPORTABLE_FIELDS.map(f => f.key)));
    }
  }, []);

  const toggleExportField = useCallback((fieldKey: string) => {
    const field = EXPORTABLE_FIELDS.find(f => f.key === fieldKey);
    if (field?.required) return;

    const newSelection = new Set(selectedExportFields);
    if (newSelection.has(fieldKey)) {
      newSelection.delete(fieldKey);
    } else {
      newSelection.add(fieldKey);
    }
    setSelectedExportFields(newSelection);
  }, [selectedExportFields]);

  const handleExportWithFields = useCallback(async () => {
    if (!userId) return;

    try {
      setExportingData(true);
      const selectedFieldsArray = Array.from(selectedExportFields);
      const csvContent = await exportService.exportRSVPToCSV(inviteCode, userId, selectedFieldsArray);

      const filename = `rsvp-data-${inviteCode}-${new Date().toISOString().split('T')[0]}.csv`;
      downloadCSV(csvContent, filename);

      setShowMainExportOptions(false);
    } catch (error) {
      log.error('Error exporting RSVP data:', error);
      alert('匯出失敗，請稍後再試');
    } finally {
      setExportingData(false);
    }
  }, [userId, inviteCode, selectedExportFields, downloadCSV, setExportingData]);

  return {
    showMainExportOptions,
    setShowMainExportOptions,
    selectedExportFields,
    setQuickExportPreset,
    toggleExportField,
    handleExportWithFields,
  };
}
